import { apiGet } from "../api-client"

export interface PolicyRule {
  id: number
  rule_id: string
  citation: string
  title: string
  source_urls: string[]
  jurisdiction: string
  doc_type: string
  topic: string
  version_tag: string
  effective_from: string | null
  effective_to: string | null
  html_sha256: string
  created_at: string
  updated_at: string
}

export interface PolicySection {
  id: number
  rule: PolicyRule
  version_tag: string
  path: string
  heading: string
  text: string
  anchor: string
  order_idx: number
  created_at: string
}

export interface PolicySectionResponse {
  count?: number
  results: PolicySection[]
}

export interface DistrictOverlay {
  id: number;
  jurisdiction: string;
  district_code: string;
  source_file: string;
  requirements: Record<string, unknown>;
  preferences: Record<string, unknown>;
  plugging_chart: Record<string, unknown>;
  imported_at: string;
}

export interface CountyOverlay {
  id: number;
  county_name: string;
  notes?: string[];
  requirements: Record<string, unknown>;
  preferences: Record<string, unknown>;
  county_procedures: Record<string, unknown>;
  formation_data: Record<string, unknown>;
}

/**
 * Fetch district overlays, optionally filtered by jurisdiction
 */
export async function fetchDistrictOverlays(jurisdiction?: string): Promise<DistrictOverlay[]> {
  const params = new URLSearchParams()
  if (jurisdiction) params.append("jurisdiction", jurisdiction)
  const endpoint = `/api/policy/district-overlays/?${params.toString()}`
  const response = await apiGet<DistrictOverlay[] | { results: DistrictOverlay[] }>(endpoint)
  return Array.isArray(response) ? response : (response.results || [])
}

/**
 * Fetch county entries for a specific district
 */
export async function fetchDistrictCounties(districtCode: string): Promise<CountyOverlay[]> {
  const endpoint = `/api/policy/district-overlays/${districtCode}/counties/`
  const response = await apiGet<CountyOverlay[] | { results: CountyOverlay[] }>(endpoint)
  return Array.isArray(response) ? response : (response.results || [])
}

/**
 * Fetch all policy rules
 */
export async function fetchPolicies(
  ruleId?: string,
  versionTag?: string
): Promise<PolicyRule[]> {
  const params = new URLSearchParams()
  params.append("page_size", "500")

  if (ruleId) params.append("rule_id", ruleId)
  if (versionTag) params.append("version_tag", versionTag)

  const endpoint = `/api/policy/rules/?${params.toString()}`

  const response = await apiGet<PolicyRule[] | { results: PolicyRule[] }>(endpoint)
  return Array.isArray(response) ? response : (response.results || [])
}

/**
 * Fetch policy sections for a specific rule
 */
export async function fetchPolicySections(
  ruleId?: string,
  versionTag?: string
): Promise<PolicySection[]> {
  let endpoint = "/api/policy/sections/"
  const params = new URLSearchParams()

  if (ruleId) params.append("rule__rule_id", ruleId)
  if (versionTag) params.append("version_tag", versionTag)

  if (params.toString()) {
    endpoint += `?${params.toString()}`
  }

  const response = await apiGet<PolicySectionResponse>(endpoint)
  return Array.isArray(response) ? response : response.results || []
}

/**
 * Fetch a single policy rule by ID
 */
export async function fetchPolicy(ruleId: string): Promise<PolicyRule> {
  const endpoint = `/api/policy/rules/${ruleId}/`
  return apiGet<PolicyRule>(endpoint)
}

/**
 * Fetch all policy sections for a rule
 */
export async function fetchRuleSections(
  ruleId: string,
  versionTag?: string
): Promise<PolicySection[]> {
  let endpoint = `/api/policy/sections/?rule__rule_id=${ruleId}`
  if (versionTag) {
    endpoint += `&version_tag=${versionTag}`
  }
  const response = await apiGet<PolicySectionResponse>(endpoint)
  return Array.isArray(response) ? response : response.results || []
}
